import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { ref, getBlob } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import JSZip from 'jszip';
import { db, storage, auth } from '../firebase/config';
import { Factory, RefreshCw, Download, Search, AlertTriangle, ShieldCheck } from 'lucide-react';
import PageHeader from './common/PageHeader';
import { useJigConsent, JigConsentGate } from './common/JigConsentGate';   // 🔐 지그 첫 이용 동의(공용) — /jig 와 같은 게이트

// 지그 결과 — (a) 클라우드 2주 보관 방식 (2026-10-19)
// 서버 계약: docs/계약_지그웹a_클라우드보관_2026-10.md (jigResults 스키마·jig-results Storage·logJigAccess·rules)
// ⚠️ 환자정보: 이 화면에는 치과·환자명·치식이 보인다. jigResults(본인 labId) 문서에서만 오고(추측·임의 생성 없음),
//    Firestore/Storage rules 가 본인 기공소만 read 를 강제한다. 기공소 전용·환자 비노출 화면이라 가격·후기·환자유인 문구 없음(의료법).
// 노출 = isJigWebOn(jigWeb 플래그 ON 또는 베타) && 기공소 — App.js 라우트·MainLayout 메뉴에서 게이팅.

// 크기 표기
function fmtSize(bytes) {
    if (bytes == null || isNaN(bytes)) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Firestore Timestamp → Date (null 안전)
function toDate(ts) {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') return ts.toDate();
    if (ts instanceof Date) return ts;
    if (typeof ts === 'number') return new Date(ts);
    return null;
}

function fmtDate(ts) {
    const d = toDate(ts);
    if (!d) return '-';
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// 만료까지 남은 일수 (올림). null 이면 null.
function daysLeft(expiresAt) {
    const d = toDate(expiresAt);
    if (!d) return null;
    return Math.ceil((d.getTime() - Date.now()) / 86400000);
}

// 다운로드 파일명 (환자·치과명 기반, 금지문자 제거) — 로컬 저장 파일명일 뿐 Storage 경로 아님
function safeName(r) {
    const base = [r.patient, r.clinic].filter(Boolean).join('_') || r.jobId || 'jig';
    return String(base).replace(/[\\/:*?"<>|]+/g, '_').slice(0, 80);
}

function JigWeb({ user }) {
    const labId = user?.companyId || auth.currentUser?.uid;
    const consent = useJigConsent();   // 🔐 지그 첫 이용 동의(공용) — 동의 전에는 jigResults(환자정보·다운로드) 비노출

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [search, setSearch] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [checked, setChecked] = useState({});
    const [busy, setBusy] = useState(false);

    const load = async () => {
        if (!labId) { setLoading(false); return; }
        setLoading(true);
        setLoadError('');
        try {
            // 본인 기공소(labId) 것만 — rules 가 read 를 본인으로 제한. labId ASC + createdAt DESC 복합 인덱스 사용.
            const snap = await getDocs(query(
                collection(db, 'jigResults'),
                where('labId', '==', labId),
                orderBy('createdAt', 'desc'),
            ));
            setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setChecked({});
        } catch (e) {
            console.error('[지그 결과] 목록 로드 실패:', e);
            setLoadError('목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        } finally {
            setLoading(false);
        }
    };
    // 동의 전에는 jigResults(환자정보)를 불러오지 않는다(fail-closed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (consent.agreed) load(); }, [labId, consent.agreed]);

    // 검색(치과·환자·치식) + 기간(createdAt) 클라이언트 필터
    const visible = useMemo(() => {
        const kw = search.trim().toLowerCase();
        const fromMs = from ? Date.parse(from + 'T00:00:00') : null;
        const toMs = to ? Date.parse(to + 'T23:59:59') : null;
        return rows.filter(r => {
            if (kw) {
                const hay = [r.jigNo, r.clinic, r.patient, ...(Array.isArray(r.teeth) ? r.teeth : [])]
                    .filter(Boolean).join(' ').toLowerCase();
                if (!hay.includes(kw)) return false;
            }
            const d = toDate(r.createdAt);
            const ms = d ? d.getTime() : null;
            if (fromMs != null && (ms == null || ms < fromMs)) return false;
            if (toMs != null && (ms == null || ms > toMs)) return false;
            return true;
        });
    }, [rows, search, from, to]);

    const allChecked = visible.length > 0 && visible.every(r => checked[r.id]);
    const checkedRows = visible.filter(r => checked[r.id]);

    const toggleAll = () => {
        if (allChecked) { setChecked({}); return; }
        const next = {};
        visible.forEach(r => { next[r.id] = true; });
        setChecked(next);
    };
    const toggleOne = (id) => setChecked(prev => ({ ...prev, [id]: !prev[id] }));

    // 접속기록(다운로드) — 다운로드를 막지는 않는다. 실패해도 조용히 넘어가지 않고 기록만(계약 §5 logJigAccess).
    const logAccess = async (jobId, action) => {
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'logJigAccess');
            await fn({ jobId, action });
        } catch (e) {
            console.error('[지그 결과] 접속기록 실패(다운로드는 계속):', e?.message || e);
        }
    };

    // Storage 에서 blob 받기 (jig-results/{labId}/{jobId}.{ext} — 서버가 파생한 stlPath 사용)
    const fetchBlob = async (r) => {
        const path = r.stlPath || `jig-results/${labId}/${r.jobId || r.id}.${r.resultExt || 'zip'}`;
        return getBlob(ref(storage, path));
    };

    const triggerDownload = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const downloadOne = async (r) => {
        if (busy) return;
        setBusy(true);
        try {
            const blob = await fetchBlob(r);
            const ext = r.resultExt || 'zip';
            triggerDownload(blob, `${safeName(r)}.${ext}`);
            await logAccess(r.jobId || r.id, 'download');
        } catch (e) {
            console.error('[지그 결과] 다운로드 실패:', e);
            alert('내려받기에 실패했습니다. 파일이 만료(삭제)되었을 수 있습니다.');
        } finally {
            setBusy(false);
        }
    };

    const downloadSelected = async () => {
        if (busy || checkedRows.length === 0) return;
        setBusy(true);
        try {
            if (checkedRows.length === 1) { await downloadOne(checkedRows[0]); setBusy(false); return; }
            const zip = new JSZip();
            const used = {};
            let ok = 0;
            for (const r of checkedRows) {
                try {
                    const blob = await fetchBlob(r);
                    const ext = r.resultExt || 'zip';
                    let name = `${safeName(r)}.${ext}`;
                    if (used[name]) { name = `${safeName(r)}_${r.jobId || r.id}.${ext}`; }
                    used[name] = true;
                    zip.file(name, blob);
                    ok += 1;
                    await logAccess(r.jobId || r.id, 'download');
                } catch (e) {
                    console.error('[지그 결과] 묶음 중 1건 실패(건너뜀):', r.id, e);
                }
            }
            if (ok === 0) { alert('선택한 파일을 모두 내려받지 못했습니다. 만료되었을 수 있습니다.'); return; }
            const out = await zip.generateAsync({ type: 'blob' });
            triggerDownload(out, `지그결과_${ok}건.zip`);
            if (ok < checkedRows.length) alert(`${checkedRows.length}건 중 ${ok}건만 받았습니다. 나머지는 만료되었을 수 있습니다.`);
        } catch (e) {
            console.error('[지그 결과] 묶음 다운로드 실패:', e);
            alert('묶음 내려받기에 실패했습니다.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <PageHeader
                icon={Factory}
                title={'지그 결과'}
                subtitle={'맥북 작업자가 만든 지그 결과를 내려받습니다. 결과는 생성일부터 7일 뒤 자동으로 삭제됩니다.'}
            />

            {consent.loading ? (
                <div style={styles.body}><div style={styles.empty}>{'불러오는 중…'}</div></div>
            ) : !consent.agreed ? (
                /* 🔐 첫 이용 동의 게이트(공용) — 동의 기록 전에는 결과 목록을 열지 않는다(법무 §3) */
                <div style={styles.body}><JigConsentGate consent={consent} padded={false} /></div>
            ) : (
            <div style={styles.body}>
                {/* 보관 안내 */}
                <div style={styles.noteCard}>
                    <ShieldCheck size={18} color="#0f766e" style={{ flexShrink: 0, marginTop: 2 }} />
                    <div>
                        <div style={styles.noteTitle}>{'지그 결과는 7일만 보관됩니다'}</div>
                        <p style={styles.noteText}>
                            {'결과 파일은 우리 기공소 계정에서만 보이며, 생성일부터 7일이 지나면 자동으로 삭제됩니다. 필요한 파일은 그 전에 내려받아 두세요.'}
                        </p>
                    </div>
                </div>

                {/* 검색·기간 */}
                <div style={styles.filterBar}>
                    <div style={styles.searchWrap}>
                        <Search size={15} color="#94a3b8" />
                        <input
                            style={styles.searchInput}
                            placeholder="치과·환자·치식 검색"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <div style={styles.dateWrap}>
                        <span style={styles.dateLabel}>{'생성일'}</span>
                        <input type="date" style={styles.dateInput} value={from} onChange={e => setFrom(e.target.value)} />
                        <span style={styles.dateSep}>{'~'}</span>
                        <input type="date" style={styles.dateInput} value={to} onChange={e => setTo(e.target.value)} />
                    </div>
                    <button style={styles.ghostBtn} onClick={load} disabled={loading}>
                        <RefreshCw size={14} />{'새로고침'}
                    </button>
                </div>

                {/* 선택 다운로드 바 */}
                <div style={styles.selBar}>
                    <label style={styles.selLabel}>
                        <input type="checkbox" checked={allChecked} onChange={toggleAll} disabled={visible.length === 0} />
                        {allChecked ? '전체 해제' : '전체 선택'}
                    </label>
                    <span style={styles.selCount}>{`선택 ${checkedRows.length}건 / 전체 ${visible.length}건`}</span>
                    <button
                        style={{ ...styles.primaryBtn, opacity: (busy || checkedRows.length === 0) ? 0.5 : 1 }}
                        onClick={downloadSelected}
                        disabled={busy || checkedRows.length === 0}
                    >
                        <Download size={15} />{busy ? '내려받는 중…' : '선택 다운로드'}
                    </button>
                </div>

                {/* 목록 */}
                {loading ? (
                    <div style={styles.empty}>{'불러오는 중…'}</div>
                ) : loadError ? (
                    <div style={styles.empty}>{loadError}</div>
                ) : visible.length === 0 ? (
                    <div style={styles.empty}>{rows.length === 0 ? '아직 지그 결과가 없습니다.' : '조건에 맞는 결과가 없습니다.'}</div>
                ) : (
                    <div style={styles.tableWrap}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}></th>
                                    <th style={styles.th}>{'번호'}</th>
                                    <th style={styles.th}>{'치과'}</th>
                                    <th style={styles.th}>{'환자'}</th>
                                    <th style={styles.th}>{'치식'}</th>
                                    <th style={styles.th}>{'생성일'}</th>
                                    <th style={styles.th}>{'만료일'}</th>
                                    <th style={styles.thRight}>{'크기'}</th>
                                    <th style={styles.thRight}>{'내려받기'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map(r => {
                                    const dl = daysLeft(r.expiresAt);
                                    const soon = dl != null && dl <= 3;
                                    const teeth = Array.isArray(r.teeth) ? r.teeth.join(', ') : (r.teeth || '-');
                                    return (
                                        <tr key={r.id} style={styles.tr}>
                                            <td style={styles.td}>
                                                <input type="checkbox" checked={!!checked[r.id]} onChange={() => toggleOne(r.id)} />
                                            </td>
                                            <td style={styles.tdNo}>{r.jigNo || ''}</td>
                                            <td style={styles.td}>{r.clinic || '-'}</td>
                                            <td style={styles.td}>{r.patient || '-'}</td>
                                            <td style={styles.td}>{teeth || '-'}</td>
                                            <td style={styles.td}>{fmtDate(r.createdAt)}</td>
                                            <td style={styles.td}>
                                                {fmtDate(r.expiresAt)}
                                                {soon && (
                                                    <span style={styles.soonBadge}>
                                                        <AlertTriangle size={11} />
                                                        {dl <= 0 ? '곧 삭제' : `D-${dl}`}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={styles.tdRight}>{fmtSize(r.sizeBytes)}</td>
                                            <td style={styles.tdRight}>
                                                <button style={styles.rowBtn} onClick={() => downloadOne(r)} disabled={busy}>
                                                    <Download size={13} />{'받기'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            )}
        </div>
    );
}

const styles = {
    body: { padding: 20, display: 'flex', flexDirection: 'column', gap: 14 },
    noteCard: {
        display: 'flex', gap: 10, padding: '12px 14px', backgroundColor: '#f0fdfa',
        border: '1px solid #99f6e4', borderRadius: 10, maxWidth: 820,
    },
    noteTitle: { fontSize: 14, fontWeight: 700, color: '#0f766e', marginBottom: 3 },
    noteText: { fontSize: 13, color: '#115e59', lineHeight: 1.6, margin: 0 },
    filterBar: { display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' },
    searchWrap: {
        display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px',
        border: '1px solid #cbd5e1', borderRadius: 8, backgroundColor: '#fff', minWidth: 220,
    },
    searchInput: { border: 'none', outline: 'none', fontSize: 13, flex: 1 },
    dateWrap: { display: 'flex', alignItems: 'center', gap: 6 },
    dateLabel: { fontSize: 12, color: '#64748b' },
    dateInput: { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 },
    dateSep: { color: '#94a3b8' },
    ghostBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px',
        border: '1px solid #cbd5e1', borderRadius: 8, backgroundColor: '#fff',
        color: '#334155', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
    selBar: {
        display: 'flex', alignItems: 'center', gap: 14, padding: '8px 2px', flexWrap: 'wrap',
    },
    selLabel: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#334155', cursor: 'pointer' },
    selCount: { fontSize: 12, color: '#64748b' },
    primaryBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
        backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 8,
        fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
    empty: {
        padding: '48px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 14,
        backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    },
    tableWrap: { overflowX: 'auto', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', padding: '10px 12px', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
    thRight: { textAlign: 'right', padding: '10px 12px', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
    tr: { borderBottom: '1px solid #f1f5f9' },
    td: { padding: '10px 12px', color: '#1e293b', verticalAlign: 'middle' },
    tdNo: { padding: '10px 12px', color: '#0f172a', fontWeight: 700, verticalAlign: 'middle', whiteSpace: 'nowrap' },
    tdRight: { padding: '10px 12px', color: '#1e293b', textAlign: 'right', verticalAlign: 'middle', whiteSpace: 'nowrap' },
    soonBadge: {
        display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 6, padding: '1px 6px',
        backgroundColor: '#fef2f2', color: '#dc2626', borderRadius: 999, fontSize: 11, fontWeight: 700,
    },
    rowBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px',
        border: '1px solid #cbd5e1', borderRadius: 7, backgroundColor: '#fff',
        color: '#334155', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    },
};

export default JigWeb;
